import { useEffect, useState } from "react";
import classNames from "classnames";
import { useLocation } from "react-router-dom";
import { Button } from "components/atoms/Button";
import { TitleSection } from "components/organisms/TitleSection";
import { ProgressCardSection } from "components/organisms/ProgressCardSection";
import { ViewProgressSummaryCard } from "components/organisms/ViewProgressSummaryCard";
import { PrepInformation } from "components/molecules/PrepInformation";
import { useAppDispatch, useAppSelector, useScreenSize } from "hooks";
import {
  DESKTOP_LARGE_WIDTH,
  MOBILE_WIDTH,
  TAB_WIDTH,
  TAB_LARGE_WIDTH,
} from "utils/constants";
import { Header } from "components/molecules";
import { ViewProgressSummaryInnerCardGroup } from "components/organisms/ViewProgressSummaryInnerCardGroup";
import { Typography } from "components/atoms";
import { ProgressSummaryMobile } from "components/organisms/ProgressSummaryMobile";
import { ViewAllSummaryMobile } from "components/organisms/ViewAllSummaryMobile";
import { Courseworks } from "components/organisms/Courseworks";
import { AiCardSection } from "components/organisms/AiCardSection";
import { Spinner } from "components/atoms/Icon";
import { decodeToken } from "utils/functions/decodeToken";
import { setToken } from "state/reducers/AuthSlice";
import { NotificationModal } from "components/molecules/NotificationModal";
import "./index.scss";
import { mixpanelHelper } from "utils/functions/mixPanelHelper";
import { setIsShowACTPopup } from "state/reducers/layoutSlice";
import { useLazyGetContentQuery } from "services/homeApi";

interface IProps {
  theme: "light" | "dark";
}

export const HomePage = ({ theme }: IProps) => {
  const [isProgressShow, setIsProgressShow] = useState(false);
  const [isPrepInfoShow, setIsPrepInfoShow] = useState(false);
  const [isAiShow, setIsAiShow] = useState(false);
  const [isShowMobileTestDetails, setIsShowMobileTestDetails] = useState(false);
  const [isShowMobileViewAllSummary, setIsShowMobileViewAllSummary] =
    useState(false);
  const [clickedGraphNameMobile, setClickedGraphNameMobile] = useState<
    undefined | string
  >();
  const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false);
  const [notificationContent, setNotificationContent] = useState("");
  const [isNotificationLoading, setIsNotificationLoading] = useState(false);

  const screen = useScreenSize();
  const location = useLocation();
  const { state: locationState } = useLocation();

  const payload = decodeToken();

  const userName = payload ? payload.username : "unknown";
  const userId = payload ? payload.userid : null;
  const productCode = payload ? payload.productcode : null;
  const offeringCode = payload ? payload.offeringcode : null;

  const { token } = useAppSelector((state) => state.auth);
  const { isShowACTPopup } = useAppSelector((state) => state.layout);
  const dispatch = useAppDispatch();

  const [getContent] = useLazyGetContentQuery();

  const [isShowStudyRecommendation] = useState(
    payload &&
    payload.hasOwnProperty("showstudyrecommendations") &&
    payload.showstudyrecommendations.toLowerCase() === "true"
  );

  const [isShowOverallProgressSummary] = useState(
    payload &&
    payload.hasOwnProperty("showoverallprogresssummary") &&
    payload.showoverallprogresssummary.toLowerCase() === "true"
  );

  const [isShowPerformanceSummary] = useState(
    payload &&
    payload.hasOwnProperty("showperformancesummary") &&
    payload.showperformancesummary.toLowerCase() === "true"
  );

  useEffect(() => {
    if (locationState && locationState.fromStudyRecommendationTool === true) {
      setIsAiShow(true);
      window.history.replaceState({}, "");
    }
  }, [locationState]);

  useEffect(() => {
    if (location.search !== "") {
      const newToken = location.search.split("=")[1];
      
      const storedToken = localStorage.getItem("lastToken");
      const isNewToken = storedToken !== newToken;
      
      dispatch(setToken(newToken));
      
      if (isNewToken) {
        dispatch(setIsShowACTPopup(true));
        sessionStorage.removeItem("notificationDismissed");
        
        // Update stored token after all checks
        localStorage.setItem("lastToken", newToken);
      }
    }
  }, [location.search]);
  
  const shouldShowNotificationModal = () => {

    if (payload && 
        payload.hasOwnProperty("shownotificationpopup") && 
        payload.shownotificationpopup.toLowerCase() === "false") {
      return false;
    }
    
    const wasOnJumpPage = isShowACTPopup;
  
    const hasSessionDismissed = sessionStorage.getItem("notificationDismissed") === "true";
    

    

    
    return wasOnJumpPage && !hasSessionDismissed;
  };
  
  const fetchNotificationContent = async (contentKey: string) => {
    try {
      setIsNotificationLoading(true);

      const response = await getContent({ contentkey: contentKey }).unwrap();
      
      if (response && response.TextData) {
        setNotificationContent(response.TextData);
      } else {
        setNotificationContent("Welcome to your Student Dashboard");
      }
    } catch (error) {
      console.error("Failed to fetch notification content:", error);
      setNotificationContent("Welcome to your Student Dashboard");
    } finally {
      setIsNotificationLoading(false);
    }
  };

  const handleNotificationModalClose = () => {
    setIsNotificationModalOpen(false);
    sessionStorage.setItem("notificationDismissed", "true");
    dispatch(setIsShowACTPopup(false));
  };

  const handleCardClick = (
    cardType: string
  ) => {
    if (cardType === "doughnut") {
      setIsProgressShow(true);
      setIsPrepInfoShow(false);
    } else {
      setIsProgressShow(false);
      setIsPrepInfoShow(true);
    }

    switch (cardType) {
      case "doughnut":
        setIsProgressShow(true);
        setIsPrepInfoShow(false);
        setIsAiShow(false);
        break;
      case "horizontal":
        setIsProgressShow(false);
        setIsPrepInfoShow(true);
        setIsAiShow(false);
        break;
      case "ai":
        setIsProgressShow(false);
        setIsPrepInfoShow(false);
        setIsAiShow(true);

        break;
      default:
        break;
    }
  };

  const handleInnerCardBtnClick = (cardType: string) => {
    if (cardType === "ai") {
      setIsProgressShow(false);
      setIsPrepInfoShow(false);
      setIsAiShow(true);
    } else if (cardType === "score") {
      setIsProgressShow(false);
      setIsPrepInfoShow(true);
      setIsAiShow(false);
    } else {
      setIsProgressShow(true);
      setIsPrepInfoShow(false);
      setIsAiShow(false);
    }
  };

  const handlePreInfoClick = () => {
    window.open("/view-prep", "__blank");
    mixpanelHelper.track("View Prep Information", {
      testType: "",
      productCode: productCode,
      psoCode: offeringCode,
      userID: userId,
    });
  };

  useEffect(() => {
    if (screen.width > MOBILE_WIDTH) {
      setIsShowMobileTestDetails(false);
      setIsShowMobileViewAllSummary(false);
    }
  }, [screen.width]);

  useEffect(() => {
    if (payload) {
      if (shouldShowNotificationModal()) {
        const contentKey = payload.hasOwnProperty("notificationcmskey") && payload.notificationcmskey
          ? payload.notificationcmskey
          : null;
        
        if (contentKey) {
          if (!isNotificationLoading && !notificationContent) {
            fetchNotificationContent(contentKey);
          }
        } else {
          setNotificationContent("Welcome to your Student Dashboard");
        }
        setIsNotificationModalOpen(true);
      }

      // Track page view
      if (productCode && offeringCode && userId) {
        mixpanelHelper.track("View Dashboard Homepage", {
          testType: "",
          productCode: productCode,
          psoCode: offeringCode,
          userID: userId,
        });
      }
    }
  }, [
    payload,
    productCode,
    offeringCode,
    userId,
    isShowACTPopup,
    isNotificationLoading,
    notificationContent,
  ]);

  if (token !== "") {
    return isShowMobileTestDetails ? (
      <ProgressSummaryMobile
        setIsShowMobileTestDetails={setIsShowMobileTestDetails}
        clickedGraphNameMobile={clickedGraphNameMobile}
        theme={theme}
      />
    ) : isShowMobileViewAllSummary ? (
      <ViewAllSummaryMobile
        setIsShowMobileViewAllSummary={setIsShowMobileViewAllSummary}
        theme={theme}
      />
    ) : (
        <>
          <NotificationModal 
            isOpen={isNotificationModalOpen}
            handleOnClose={handleNotificationModalClose}
            theme={theme}
            notificationContent={notificationContent}
          isLoading={isNotificationLoading}
          />
      <div className="home-page-main-wrapper">
        <div className={classNames("header-section")}>
          <Header
            isMobile={screen.width < MOBILE_WIDTH}
            theme={theme}
            leftContentLabel={`Welcome, ${userName}!`}
            isTab={screen.width <= TAB_LARGE_WIDTH}
            isAlert={screen.width <= TAB_LARGE_WIDTH}
          />
        </div>
        <div className={classNames("home-page-top-label-section")}>
          <Button
            type="filter"
            label={
              screen.width < MOBILE_WIDTH
                ? "View Your Prep Information"
                : "Click here to start! View your prep info"
            }
            onClick={handlePreInfoClick}
            styles="custom-typography-style"
            icon={false}
            btnId="dashboard-view-prep-info-btn"
          />
        </div>
        <div className={classNames("home-page-progress-card-section")}>
          {isProgressShow ? (
            <ViewProgressSummaryCard
              setIsShowMobileTestDetails={setIsShowMobileTestDetails}
              setIsShowMobileViewAllSummary={setIsShowMobileViewAllSummary}
              setClickedGraphNameMobile={setClickedGraphNameMobile}
              theme={theme}
            />
          ) : isPrepInfoShow ? (
            <PrepInformation
              isMobile={screen.width < MOBILE_WIDTH}
              isTab={screen.width <= TAB_WIDTH}
              theme={theme}
            />
          ) : isAiShow ? (
            <AiCardSection theme={theme} />
          ) : (
            <ProgressCardSection theme={theme} onClick={handleCardClick} />
          )}
        </div>
        <div
          className={classNames({
            "view-progress-card-inner-card-group": true,
            "is-hide-ai-nav-card": !isShowStudyRecommendation,
            "is-hide-progress-summary-nav-card": !isShowOverallProgressSummary,
            "is-hide-performance-summary-nav-card": !isShowPerformanceSummary,
          })}
        >
          {[isShowOverallProgressSummary, isShowPerformanceSummary, isShowStudyRecommendation]
            .filter(Boolean).length > 1
            ? (isPrepInfoShow || isProgressShow || isAiShow ? (
              <ViewProgressSummaryInnerCardGroup
                btnLabel={
                  isPrepInfoShow
                    ? "View Progress Summary"
                    : isAiShow
                      ? "View Progress Summary"
                      : "View Scores"
                }
                cardLabel={
                  isPrepInfoShow
                    ? "Overall Progress Summary"
                    : isAiShow
                      ? "Overall Progress Summary"
                      : "Test Scores & Performance"
                }
                secondaryBtnLabel={
                  isPrepInfoShow
                    ? "View Recommendations"
                    : isAiShow
                      ? "View Scores"
                      : "View Recommendations"
                }
                secondaryCardLabel={
                  isPrepInfoShow
                    ? "Study Recommendations"
                    : isAiShow
                      ? "Test Scores & Performance"
                      : "Study Recommendations"
                }
                thirdBtnLabel={
                  isProgressShow
                    ? "View Recommendations"
                    : isAiShow
                      ? "View Progress Summary"
                      : "View Recommendations"
                }
                thirdCardLabel={
                  isProgressShow
                    ? "Study Recommendations"
                    : isAiShow
                      ? "Overall Progress Summary"
                      : "Study Recommendations"
                }
                onClick={handleInnerCardBtnClick}
              />
            ) : null) : null}
        </div>
        <div className={classNames("home-page-title-section")}>
          <TitleSection theme={theme} />
        </div>
        <Courseworks theme={theme} />
      </div>
      </>
    );
  } else {
    return <Spinner theme={theme} />;
  }
};